import { Coverage, Organization } from "@medplum/fhirtypes";
import { FhirBundleSdk, Smart } from "@metriport/fhir-sdk";

export type CoverageRowData = {
  id: string;
  provider: string;
  policyId: string;
  status: string;
  originalData: Smart<Coverage>;
};

export function getCoverageRows({ bundle }: { bundle: FhirBundleSdk }): CoverageRowData[] {
  try {
    const coverages = bundle.getCoverages();
    const organizations = bundle.getOrganizations();

    return coverages.map(coverage => {
      // Extract provider/payor information (resolved like V1)
      const provider = getCoverageProvider(coverage, organizations);

      // Extract policy ID (gather all identifiers like V1)
      const policyId = getPolicyId(coverage);

      // Extract status
      const status = coverage.status || "-";

      return {
        id: coverage.id || "-",
        provider,
        policyId,
        status,
        originalData: coverage,
      };
    });
  } catch (error) {
    console.warn("Coverage not available in FhirBundleSdk:", error);
    return [];
  }
}

function getCoverageProvider(coverage: Coverage, organizations: Organization[]): string {
  const payorRef = coverage.payor?.[0]?.reference?.split("/")?.[1];

  if (payorRef) {
    const organization = organizations.find(org => org.id === payorRef);
    if (organization) {
      return organization.name ?? "-";
    }
  }

  // Fallback to display text if available
  const payorDisplay = coverage.payor?.[0]?.display;
  if (payorDisplay) {
    return payorDisplay;
  }

  return "-";
}

function getPolicyId(coverage: Coverage): string {
  const policyIdSet = new Set<string>();

  // Add subscriber ID
  if (coverage.subscriberId) {
    policyIdSet.add(coverage.subscriberId);
  }

  // Add all identifiers
  if (coverage.identifier) {
    for (const identifier of coverage.identifier) {
      if (identifier.value) {
        policyIdSet.add(identifier.value);
      }
    }
  }

  return Array.from(policyIdSet).join(", ");
}
